import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/store'
import axios from 'axios'
import { isModuleEnabled } from '@/utils/moduleControl'
import governorates from './routes/location/governorates'
import delegations from './routes/location/delegations'
import localities from './routes/location/localities'
import repositories from './routes/repositories'
import cars from './routes/cars'
import suppliers from './routes/users/suppliers'
import deliveryMans from './routes/users/deliveryMans'
import pickupeurs from './routes/users/pickupeurs'
import managers from './routes/users/managers'
import staff from './routes/users/staff'
import orders from './routes/orders'
import pickups from './routes/pickups'
import deliveryNotes from './routes/deliveryNotes'
import roadmaps from './routes/roadmaps'
import payments from './routes/payments'
import invoices from './routes/invoices'
import transferVouchers from './routes/transferVouchers'
import returnVouchers from './routes/returnVouchers'
import tobeReimbursedVouchers from './routes/tobeReimbursedVouchers'
import weights from './routes/weights'
import synchronization from './routes/synchronization'
import reporting from './routes/reporting'
import exchange from './routes/exchange'
import requestPartner from './routes/requestPartner'
import orderSearchHistory from './routes/orderSearchHistory'
import support from './routes/support'
import returnReasonTypes from './routes/returnReasonTypes'
import ticketCategories from './routes/ticketCategories'
import expenses from './routes/expenses'
import suiviInstantane from './routes/suiviInstantane'
import commissions from './routes/commissions'
import wallets from './routes/wallets'
import inventory from './routes/inventory'
import manifestsPickup from './routes/manifestsPickup'
import driverInvoices from './routes/driverInvoices'
import exchangeManifests from './routes/exchangeManifests'
import extraFees from './routes/extraFees'
import paymentSousTraitance from './routes/paymentSousTraitance'
import outsourcingPricing from './routes/outsourcingPricing'

Vue.use(VueRouter)

const SALES_ALLOWED_ROUTES = new Set([
  'dashboard',
  'profile',
  'sales-invoices',
  'sales-invoice-detail',
  'sales-suppliers',
  'supplier-list',
  'supplier-add',
  'supplier-edit',
  'supplier-show',
  'my-wallet',
  'logout',
])

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'dashboard',
      props: true,
      component: () => import('@/views/DashboardWrapper.vue'),
      meta: {
        layout: 'vertical',
        requireLogin: true,
        pageTitle: 'Tableau de bord',
        breadcrumb: [
          {
            text: 'Tableau de bord',
            active: true,
          },
        ],
      },
    },
    {
      path: '/profil/:tab?',
      name: 'profile',
      component: () => import('@/views/Authentification/Profil.vue'),
      meta: {
        requireLogin: true,
      },
    },
    {
      path: '/login',
      name: 'auth',
      component: () => import('@/views/Authentification/Login.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/Authentification/Logout.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/error-403',
      name: 'error-403',
      component: () => import('@/views/error/Error403.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/forbidden',
      name: 'forbidden',
      component: () => import('@/views/errors/Forbidden.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '*',
      redirect: 'error-404',
    },
    ...(isModuleEnabled('governorates') ? governorates : []),
    ...(isModuleEnabled('delegations') ? delegations : []),
    ...(isModuleEnabled('localities') ? localities : []),
    ...(isModuleEnabled('repositories') ? repositories : []),
    ...(isModuleEnabled('cars') ? cars : []),
    ...(isModuleEnabled('suppliers') ? suppliers : []),
    ...(isModuleEnabled('deliveryMans') ? deliveryMans : []),
    ...(isModuleEnabled('pickupeurs') ? pickupeurs : []),
    ...(isModuleEnabled('managers') ? managers : []),
    ...(isModuleEnabled('staff') ? staff : []),
    ...(isModuleEnabled('orders') ? orders : []),
    ...(isModuleEnabled('orderSearchHistory') ? orderSearchHistory : []),
    ...(isModuleEnabled('pickups') ? pickups : []),
    ...(isModuleEnabled('inventory') ? inventory : []),
    ...(isModuleEnabled('deliveryNotes') ? deliveryNotes : []),
    ...(isModuleEnabled('requestPartner') ? requestPartner : []),
    ...(isModuleEnabled('roadmaps') ? roadmaps : []),
    ...(isModuleEnabled('payments') ? payments : []),
    ...(isModuleEnabled('invoices') ? invoices : []),
    ...(isModuleEnabled('transferVouchers') ? transferVouchers : []),
    ...(isModuleEnabled('returnVouchers') ? returnVouchers : []),
    ...(isModuleEnabled('toBeReimbursedVouchers') ? tobeReimbursedVouchers : []),
    ...(isModuleEnabled('weights') ? weights : []),
    ...(isModuleEnabled('synchronization') ? synchronization : []),
    ...(isModuleEnabled('reporting') ? reporting : []),
    ...(isModuleEnabled('exchange') ? exchange : []),
    ...(isModuleEnabled('support') ? support : []),
    ...(isModuleEnabled('returnReasonTypes') ? returnReasonTypes : []),
    ...(isModuleEnabled('ticketCategories') ? ticketCategories : []),
    ...(isModuleEnabled('expenses') ? expenses : []),
    ...suiviInstantane,
    ...(isModuleEnabled('commissions') ? commissions : []),
    ...wallets,
    ...manifestsPickup,
    ...(isModuleEnabled('driverInvoices') ? driverInvoices : []),
    ...(isModuleEnabled('exchangeManifests') ? exchangeManifests : []),
    ...(isModuleEnabled('extraFees') ? extraFees : []),
    ...(isModuleEnabled('paymentSousTraitance') ? paymentSousTraitance : []),
    ...(isModuleEnabled('outsourcingPricing') ? outsourcingPricing : []),
  ],
})

router.beforeEach((to, from, next) => {
  store.commit('initializeStore')
  const { token } = store.state
  const role = store.getters.userRole

  if (token) {
    axios.defaults.headers.common.Authorization = `Token ${token}`
  } else {
    axios.defaults.headers.common.Authorization = ''
  }

  if (to.matched.some(record => record.meta.requireLogin)) {
    if (!store.state.isAuthenticated) {
      next({ name: 'auth' })
    } else if (role === 'sales' && to.name && !SALES_ALLOWED_ROUTES.has(to.name)) {
      next({ name: 'error-403' })
    } else if (to.meta.roles && !to.meta.roles.includes(role)) {
      // Redirect to an error page or any appropriate action
      next({ name: 'error-403' }) // Unauthorized access
    } else if (to.matched.some(record => record.meta.requiresAdmin) && !['admin', 'superadmin'].includes(role)) {
      // Check if route requires admin access
      next({ name: 'error-403' }) // Unauthorized access
    } else {
      next()
    }
  } else {
    next()
  }
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

// Global error handler for NavigationDuplicated errors
const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace

VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

// Axios global 403 error handler: redirect to /forbidden
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 403) {
      router.push({ name: 'forbidden' })
    }
    return Promise.reject(error)
  },
)

export default router
